import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDynamicLeadReassignment } from '@/hooks/useDynamicLeadReassignment';
import { Loader2 } from 'lucide-react';
import { useUsers } from '@/hooks/useUsers';

export default function AdminUtilities() {
  const [agentPattern, setAgentPattern] = useState('');
  const [selectedOldCsr, setSelectedOldCsr] = useState('');
  const [selectedNewCsr, setSelectedNewCsr] = useState('');
  
  const { data: users } = useUsers();
  const reassignMutation = useDynamicLeadReassignment();

  const handleDynamicReassign = () => {
    if (!agentPattern || !selectedOldCsr || !selectedNewCsr) {
      return;
    }
    
    reassignMutation.mutate({
      agentNamePattern: agentPattern,
      oldCsrId: selectedOldCsr,
      newCsrId: selectedNewCsr,
      reason: `Reassigned leads matching agent "${agentPattern}"`
    });
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Admin Utilities</h1>
        <p className="text-muted-foreground">Bulk operations and data fixes</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Dynamic Lead Reassignment by Agent Name</CardTitle>
          <CardDescription>
            Reassign all webhook leads that match an agent name pattern
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="agent-pattern">Agent Name Pattern</Label>
              <Input
                id="agent-pattern"
                placeholder="e.g., Krizzy M"
                value={agentPattern}
                onChange={(e) => setAgentPattern(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="old-csr">Current CSR (Old)</Label>
              <select
                id="old-csr"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={selectedOldCsr}
                onChange={(e) => setSelectedOldCsr(e.target.value)}
              >
                <option value="">Select CSR...</option>
                {users?.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.full_name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new-csr">New CSR</Label>
              <select
                id="new-csr"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={selectedNewCsr}
                onChange={(e) => setSelectedNewCsr(e.target.value)}
              >
                <option value="">Select CSR...</option>
                {users?.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.full_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <Button 
            onClick={handleDynamicReassign}
            disabled={!agentPattern || !selectedOldCsr || !selectedNewCsr || reassignMutation.isPending}
            className="w-full"
          >
            {reassignMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Reassign Leads
          </Button>

          {reassignMutation.isSuccess && reassignMutation.data && (
            <div className="p-4 bg-success/10 rounded-md">
              <p className="font-semibold text-success">Success!</p>
              <p className="text-sm text-success/90">
                Updated {reassignMutation.data.updated_count} leads
              </p>
              <p className="text-sm text-success/90">
                Created {reassignMutation.data.activities_created} activity logs
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
